import {
  ArrowLineDownIcon,
  ArrowLineTopIcon,
  CurecallButton,
  CurecallInputLabel,
  ECurecallButtonAppearance,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useEffect, useId, useState } from 'react'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useFormContext } from 'react-hook-form'
import { PlanningTabFormValues } from './../planning.type'

type TOrderedTab = {
  id?: number
  label: string
}

export function PlanningTabPositionField() {
  const { t } = useTranslation()
  const id = useId()
  const { watch, setValue } = useFormContext<PlanningTabFormValues>()
  const { planningTabs: existingTabs } = useCurrentProject()

  const currentTabLabel = watch('label') || t('pages.planning.form.new-tab-placeholder')
  const updatedTabId = watch('id')

  const [orderedTabs, setOrderedTabs] = useState<TOrderedTab[]>(
    updatedTabId
      ? existingTabs.map((tab) => ({ id: tab.id, label: tab.label }))
      : [
          ...existingTabs.map((tab) => ({ id: tab.id, label: tab.label })),
          { id: undefined, label: currentTabLabel },
        ],
  )

  const onMoveColumn = (movedTab: TOrderedTab, newPosition: number) => {
    if (newPosition < 0 || newPosition >= existingTabs.length + 1) return
    const newTabs = [...orderedTabs]
    const currentIndex = newTabs.findIndex((t) => t.id === movedTab.id)
    if (currentIndex !== -1) {
      newTabs.splice(currentIndex, 1) // Remove the tab from its current position
      newTabs.splice(newPosition, 0, movedTab) // Insert it at the new position
      setOrderedTabs(newTabs)
      setValue('tabPosition', newPosition + 1)
    }
  }

  useEffect(() => {
    setOrderedTabs((prev) => {
      const updatedTabs = [...prev].map((tab) => {
        if (tab.id === undefined) {
          return { id: undefined, label: currentTabLabel }
        }
        if (tab.id === updatedTabId) {
          return { id: updatedTabId, label: currentTabLabel }
        }
        return tab
      })
      return updatedTabs
    })
  }, [currentTabLabel, updatedTabId])

  return (
    <div>
      <CurecallInputLabel
        htmlFor={id}
        label={t('pages.planning.form.tab-position')}
        isRequired={true}
        tooltipContent={t('pages.planning.form.tab-position-tooltip')}
        tooltipPosition="bottom"
      />
      <fieldset className="c-flex c-flex-col c-gap-1" id={id}>
        {orderedTabs.map((tab, position) => {
          const isNewTab = tab.label === currentTabLabel
          return (
            <div key={tab.id || tab.label} className="c-flex c-items-center c-gap-1">
              <p className="c-m-0">{tab.label}</p>

              {isNewTab && (
                <CurecallButton
                  ariaLabelText={t('pages.planning.form.move-up')}
                  appearance={ECurecallButtonAppearance.CANCEL}
                  iconType={<ArrowLineTopIcon />}
                  isGhostButton={true}
                  onClick={() => {
                    onMoveColumn(tab, position - 1)
                  }}
                  isDisabled={position === 0}
                  disabled={position === 0}
                />
              )}

              {isNewTab && (
                <CurecallButton
                  ariaLabelText={t('pages.planning.form.move-down')}
                  appearance={ECurecallButtonAppearance.CANCEL}
                  iconType={<ArrowLineDownIcon />}
                  isGhostButton={true}
                  onClick={() => {
                    onMoveColumn(tab, position + 1)
                  }}
                  isDisabled={position === orderedTabs.length - 1}
                  disabled={position === orderedTabs.length - 1}
                />
              )}
            </div>
          )
        })}
      </fieldset>
    </div>
  )
}
